import React, { useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { useAddVerifiableField } from '../../hooks/useAddVerifiableField';
import { CONTRACT_IQ_TYPE } from '@/config/reasonTypes.config';
import { EntityTypeEnum } from '@/graphql/contracts/_generated_/contracts';

import styles from './add-verifiable-field-modal.module.scss';

const ENTITY_TYPE_LABELS: Record<EntityTypeEnum, string> = {
  [EntityTypeEnum.BUNDLE]: 'Bundle',
  [EntityTypeEnum.CONTRACT]: 'Contract',
  [EntityTypeEnum.CONTRACT_LINE]: 'Contract Line',
  [EntityTypeEnum.CUSTOMER]: 'Customer',
  [EntityTypeEnum.PRODUCT]: 'Product',
  [EntityTypeEnum.PURCHASE_ORDER]: 'Purchase Order',
  [EntityTypeEnum.SALES_ORDER]: 'Sales Order',
  [EntityTypeEnum.UPLIFT_INDEX]: 'Uplift Index',
  [EntityTypeEnum.UPLIFT_REGION]: 'Uplift Region',
};

const ENTITY_TYPE_OPTIONS = Object.values(EntityTypeEnum).map((value) => ({
  value,
  label: ENTITY_TYPE_LABELS[value],
}));

interface AddVerifiableFieldFields {
  reasonCode: string;
  entityType: EntityTypeEnum;
  fieldName: string;
}

const validationSchema = Yup.object().shape({
  reasonCode: Yup.string().required('Reason code is required'),
  entityType: Yup.mixed<EntityTypeEnum>().oneOf(Object.values(EntityTypeEnum)).required('Entity type is required'),
  fieldName: Yup.string().required('Field name is required'),
});

interface IAddVerifiableFieldModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddVerifiableFieldModal({
  onClose,
  onSuccess,
}: IAddVerifiableFieldModalProps) {
  const { getReasonCodes, reasonCodes: allReasonCodes, isLoading: isLoadingReasonCodes } = useGetReasonCodes();
  const { addVerifiableField, isLoading: isSubmitting } = useAddVerifiableField();

  useEffect(() => {
    getReasonCodes(CONTRACT_IQ_TYPE);
  }, [getReasonCodes]);

  const reasonCodes = allReasonCodes.filter(rc => rc.reasonType?.type === CONTRACT_IQ_TYPE);

  return (
    <AgModalWindow
      isOpen={true}
      onClose={onClose}
      title="Add New Verifiable Field"
      contentStyle={{ width: 550, maxHeight: '80vh', overflowY: 'auto' }}
    >
      <Formik<AddVerifiableFieldFields>
        initialValues={{
          reasonCode: '',
          entityType: EntityTypeEnum.CONTRACT,
          fieldName: '',
        }}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={(values) => {
          addVerifiableField(
            {
              field: values.fieldName,
              reasonCode: values.reasonCode,
              entityType: values.entityType,
            },
            () => {
              onSuccess();
              onClose();
            }
          );
        }}
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched, isValid }) => (
          <Form>
            {/* Reason Code Dropdown */}
            <ModalFormDropdown
              id="reasonCode"
              labelTitle="Reason Code"
              requiredIcon={true}
              optionsData={reasonCodes}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.code}
              isLoading={isLoadingReasonCodes}
              placeholder="Select reason code..."
              selectedOption={allReasonCodes.find(rc => rc.code === values.reasonCode) ?? null}
              handleChange={(newValue) => {
                const code = newValue?.code ?? '';
                setFieldValue('reasonCode', code);
              }}
              validationMessage={
                touched.reasonCode && !values.reasonCode && errors.reasonCode
                  ? errors.reasonCode
                  : undefined
              }
            />

            {/* Field Name */}
            <div className={styles['form-field']}>
              <TopLabel
                id="fieldName"
                label="Field Name"
                required={true}
              />
              <input
                id="fieldName"
                type="text"
                className={styles['text-input']}
                value={values.fieldName}
                onChange={(e) => {
                  setFieldValue('fieldName', e.target.value);
                  setFieldTouched('fieldName', true, false);
                }}
                placeholder="Enter field name..."
              />
              {touched.fieldName && errors.fieldName && (
                <span className={styles['error-text']}>{errors.fieldName}</span>
              )}
            </div>

            {/* Entity Type */}
            <ModalFormDropdown
              id="entityType"
              labelTitle="Entity Type"
              requiredIcon={true}
              optionsData={ENTITY_TYPE_OPTIONS}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              placeholder="Select entity type..."
              selectedOption={ENTITY_TYPE_OPTIONS.find(o => o.value === values.entityType) ?? null}
              handleChange={(newValue) => {
                const val = newValue?.value ?? ('' as EntityTypeEnum);
                setFieldValue('entityType', val);
              }}
              validationMessage={
                touched.entityType && !values.entityType && errors.entityType
                  ? String(errors.entityType)
                  : undefined
              }
            />

            {/* Action Buttons */}
            <ActionButtonsWrapper
              showDivider={false}
              leftButton={(
                <Button
                  buttonStyleType={ButtonStyleType.SECONDARY}
                  onClick={onClose}
                >
                  Cancel
                </Button>
              )}
              rightButton={(
                <Button
                  disabled={!isValid || isSubmitting}
                  type="submit"
                  buttonStyleType={ButtonStyleType.PRIMARY}
                >
                  Add Field
                </Button>
              )}
            />
          </Form>
        )}
      </Formik>
    </AgModalWindow>
  );
}
